import AppConfig from '@config';
import * as assets from '@assets';
import useUserActionLog, {
  Enum_Action_Button_Enum,
  CapaActivityType
} from '@hooks/useUserActionLog';
import { clsx, colorSet } from '@components/Provider';
import { CustomizedTypography, CustomizedButton } from '@components/Customized';
import {
  useTheme,
  useMediaQuery,
  Container,
  Divider,
  IconButton,
  makeStyles
} from '@material-ui/core';
import { Instagram, Facebook, LinkedIn } from '@material-ui/icons';

const BOTTOM_NAV_HEIGHT = 56;

export const useStyles = makeStyles((theme) => ({
  root: {
    borderTop: `1px solid ${colorSet.gray400}`
  },
  container: {
    maxWidth: 1024,
    padding: '74px 32px',
    '& .MuiTypography-colorTextSecondary': {
      color: colorSet.gray600
    },
    [theme.breakpoints.down('sm')]: {
      padding: '36px 24px',
      marginBottom: BOTTOM_NAV_HEIGHT
    }
  },
  content: {
    textAlign: 'left',
    display: 'flex',
    justifyContent: 'space-between'
  },
  partOne: {
    '& img': {
      height: 24,
      width: 89,
      marginBottom: 20
    }
  },
  partTwo: {
    display: 'flex',
    '& .link': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      '& button + button': {
        marginTop: 12
      }
    },
    '& div + div': {
      marginLeft: 72
    }
  },
  iconBtn: {
    color: colorSet.gray900,
    '&+button': {
      marginLeft: 4
    },
    [theme.breakpoints.down('sm')]: {
      backgroundColor: colorSet.gray200,
      '&+button': {
        marginLeft: 12
      }
    }
  },
  contentSm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    '& img': {
      height: 24,
      width: 89,
      marginBottom: 32
    },
    '& .link-wrapper': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginBottom: 47,
      '& button + button': {
        marginTop: 12
      }
    }
  },
  iconWrapper: {
    marginBottom: 27
  },
  terms: {
    display: 'flex',
    alignItems: 'center',
    '& .divider': {
      backgroundColor: colorSet.gray400,
      height: 12,
      margin: 'auto 8px'
    },
    '& .customized-typography.caption': {
      color: colorSet.gray600
    }
  }
}));

const FooterPresenter = () => {
  const { onInsertUserActionLogOne } = useUserActionLog();
  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
  const classes = useStyles();
  const buttonLog = (actionid: Enum_Action_Button_Enum) => {
    onInsertUserActionLogOne(actionid, CapaActivityType.ButtonClick);
  };

  return (
    <div className={clsx(classes.root, 'footer')}>
      {!matchesSm ? (
        <Container className={classes.container}>
          <div className={classes.content}>
            <div className={classes.partOne}>
              <img src={assets.LOGO_MEDIUM} alt="logo" />
              <CustomizedTypography
                variant="body1"
                fontWeight="500"
                gutterBottoms={16}
              >
                문의 사항은 우측 하단의 채널톡으로 주세요.
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                gutterBottoms={16}
                color="textSecondary"
              >
                평일 오전 10시 - 오후 6시.
                <br />
                (점심 시간 1시 - 2시 제외 • 주말/공휴일 제외)
              </CustomizedTypography>
              <CustomizedTypography variant="body2" color="primary">
                일반 문의: support@capa.ai
              </CustomizedTypography>
              <CustomizedTypography
                variant="body2"
                color="primary"
                gutterBottoms={50}
              >
                비즈니스, 제휴 문의: business@capa.ai
              </CustomizedTypography>
            </div>
            <div className={classes.partTwo}>
              <div className="link">
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(Enum_Action_Button_Enum.Button_Main_Footer_Blog);
                    window.open(`${AppConfig.baseUrl}/blog`);
                  }}
                >
                  CAPA 블로그
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    window.open(`https://ateamventures.career.greetinghr.com/`);
                  }}
                >
                  CAPA CAREER
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    window.open(
                      `https://ateamventures.notion.site/CAPA-Release-Note-2023-a0305e1cf308474e89289622f6ad888b`
                    );
                  }}
                >
                  CAPA 릴리즈노트
                </CustomizedButton>
              </div>
              <div className="link">
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(
                      Enum_Action_Button_Enum.Button_Main_Footer_TermsAndService
                    );
                    window.open(`${AppConfig.baseUrl}/terms/service`);
                  }}
                >
                  서비스 이용약관
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(
                      Enum_Action_Button_Enum.Button_Main_Footer_Private
                    );
                    window.open(`${AppConfig.baseUrl}/terms/privacy`);
                  }}
                >
                  개인정보처리방침
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(
                      Enum_Action_Button_Enum.Button_Main_Footer_Private
                    );
                    window.open(`${AppConfig.baseUrl}/terms/marketing`);
                  }}
                >
                  마케팅정보수신동의
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(
                      Enum_Action_Button_Enum.Button_Main_Footer_Private
                    );
                    window.open(`${AppConfig.baseUrl}/terms/payment`);
                  }}
                >
                  캐파결제약관
                </CustomizedButton>
                <CustomizedButton
                  variant="text"
                  size="large"
                  color="default"
                  onClick={() => {
                    buttonLog(
                      Enum_Action_Button_Enum.Button_Main_Footer_Private
                    );
                    window.open(`${AppConfig.baseUrl}/terms/policy`);
                  }}
                >
                  서비스 운영 정책
                </CustomizedButton>
              </div>
            </div>
          </div>
          <div className={classes.content}>
            <div className={classes.partOne}>
              <CustomizedTypography
                variant="caption"
                color="textSecondary"
                display="block"
                gutterBottom
              >
                (주)에이팀벤처스 (대표: 고산) | 서울특별시 서초구 나루터로 60
                (잠원동, 정원빌딩), 3층
              </CustomizedTypography>
              <CustomizedTypography
                variant="caption"
                color="textSecondary"
                display="block"
              >
                사업자등록번호 : 101-86-83458 | 통신판매신고번호 :
                2017-서울서초-1387 | 팩스 : 02-6442-5450 | 전화 : 02-743-6322
              </CustomizedTypography>
            </div>
            <div className={classes.partTwo}>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open('https://instagram.com/capa.ai');
                }}
              >
                <Instagram />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open('https://www.facebook.com/capakr/');
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open(
                    'https://www.linkedin.com/company/10441242/admin/'
                  );
                }}
              >
                <LinkedIn />
              </IconButton>
            </div>
          </div>
        </Container>
      ) : (
        <Container className={classes.container} disableGutters>
          <div className={classes.contentSm}>
            <img src={assets.LOGO_MEDIUM} alt="logo" />
            <div className="link-wrapper">
              <CustomizedButton
                variant="text"
                size="large"
                color="default"
                onClick={() => {
                  buttonLog(Enum_Action_Button_Enum.Button_Main_Footer_Blog);
                  window.open(`${AppConfig.baseUrl}/blog`);
                }}
              >
                CAPA 블로그
              </CustomizedButton>
              <CustomizedButton
                variant="text"
                size="large"
                color="default"
                onClick={() => {
                  window.open(`https://ateamventures.career.greetinghr.com/`);
                }}
              >
                CAPA CAREER
              </CustomizedButton>
              <CustomizedButton
                variant="text"
                size="large"
                color="default"
                onClick={() => {
                  window.open(
                    `https://ateamventures.notion.site/CAPA-Release-Note-2023-a0305e1cf308474e89289622f6ad888b`
                  );
                }}
              >
                CAPA 릴리즈노트
              </CustomizedButton>
            </div>
            <div className={classes.iconWrapper}>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open('https://instagram.com/capa.ai');
                }}
              >
                <Instagram />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open('https://www.facebook.com/capakr/');
                }}
              >
                <Facebook />
              </IconButton>
              <IconButton
                className={classes.iconBtn}
                onClick={() => {
                  window.open(
                    'https://www.linkedin.com/company/10441242/admin/'
                  );
                }}
              >
                <LinkedIn />
              </IconButton>
            </div>
            <CustomizedTypography
              variant="body1"
              fontWeight="500"
              gutterBottoms={16}
            >
              문의 사항은 우측 하단의 채널톡으로 주세요.
            </CustomizedTypography>
            <CustomizedTypography
              variant="body2"
              color="textSecondary"
              gutterBottoms={16}
            >
              평일 오전 10시 - 오후 6시.
              <br />
              (점심 시간 1시 - 2시 제외 • 주말/공휴일 제외)
            </CustomizedTypography>
            <CustomizedTypography variant="body2" color="primary">
              일반 문의: support@capa.ai
            </CustomizedTypography>
            <CustomizedTypography
              variant="body2"
              color="primary"
              gutterBottoms={29}
            >
              비즈니스, 제휴 문의: business@capa.ai
            </CustomizedTypography>
            <div className={classes.terms}>
              <CustomizedButton
                variant="text"
                size="small"
                color="secondary"
                onClick={() => {
                  buttonLog(
                    Enum_Action_Button_Enum.Button_Main_Footer_TermsAndService
                  );
                  window.open(`${AppConfig.baseUrl}/terms/service`);
                }}
              >
                <CustomizedTypography variant="caption" color="textSecondary">
                  이용약관
                </CustomizedTypography>
              </CustomizedButton>
              <Divider className="divider" orientation="vertical" flexItem />
              <CustomizedButton
                variant="text"
                size="small"
                color="secondary"
                onClick={() => {
                  buttonLog(Enum_Action_Button_Enum.Button_Main_Footer_Private);
                  window.open(`${AppConfig.baseUrl}/terms/privacy`);
                }}
              >
                <CustomizedTypography variant="caption" color="textSecondary">
                  개인정보처리방침
                </CustomizedTypography>
              </CustomizedButton>
              <Divider className="divider" orientation="vertical" flexItem />
              <CustomizedButton
                variant="text"
                size="small"
                color="secondary"
                onClick={() => {
                  buttonLog(Enum_Action_Button_Enum.Button_Main_Footer_Private);
                  window.open(`${AppConfig.baseUrl}/terms/marketing`);
                }}
              >
                <CustomizedTypography variant="caption" color="textSecondary">
                  마케팅정보수신동의
                </CustomizedTypography>
              </CustomizedButton>
            </div>
          </div>
        </Container>
      )}
    </div>
  );
};

export default FooterPresenter;
